import React, { useState, useEffect } from 'react';
import { collection, query, where, orderBy, onSnapshot, doc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';

const TenantRequestsManager = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [processing, setProcessing] = useState(null);
  const [editingField, setEditingField] = useState(null); // Only one field at a time
  const [editValue, setEditValue] = useState('');

  // Real-time listener for pending requests
  useEffect(() => {
    const q = query(
      collection(db, 'tenant_requests'),
      where('status', '==', 'pending'),
      orderBy('submittedAt', 'desc')
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const requestsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        submittedAt: doc.data().submittedAt?.toDate()
      }));
      setRequests(requestsData);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const startEdit = (requestId, field, currentValue) => {
    setEditingField(`${requestId}-${field}`);
    setEditValue(currentValue || '');
  };

  const cancelEdit = () => {
    setEditingField(null);
    setEditValue('');
  };

  const saveEdit = async (requestId, field) => {
    try {
      const requestRef = doc(db, 'tenant_requests', requestId);
      await updateDoc(requestRef, {
        [field]: editValue
      });
      
      setRequests(prev => prev.map(req => 
        req.id === requestId 
          ? { ...req, [field]: editValue }
          : req
      ));
      
      cancelEdit();
      
    } catch (error) {
      console.error('Error updating field:', error);
      window.alert('Error saving changes. Please try again.');
    }
  };

  const handleApprove = async (request) => {
  if (!window.confirm(`Are you sure you want to approve ${request.schoolName}? This will create their tenant immediately.`)) {
    return;
  }

  setProcessing(request.id);
  try {
    const response = await fetch('https://us-east1-relief-roster-43593.cloudfunctions.net/approveTenantRequest', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ 
        requestId: request.id,
        requestData: request
      })
    });

    const result = await response.json();
    
    if (result.success) {
      window.alert(`✅ Success! Tenant created for ${request.schoolName}\nTenant ID: ${result.tenantId}\nURL: ${request.subdomain}.reliefroster.com`);
    } else {
      window.alert(`❌ Error: ${result.error}`);
    }
  } catch (error) {
    console.error('Error approving request:', error);
    window.alert(`❌ Error approving request: ${error.message}`);
  } finally {
    setProcessing(null);
  }
};

  const handleReject = async (request) => {
    const reason = window.prompt(`Why are you rejecting ${request.schoolName}?`, '');
    if (!reason) return;

    setProcessing(request.id);
    try {
      const requestRef = doc(db, 'tenant_requests', request.id);
      await updateDoc(requestRef, {
        status: 'rejected',
        rejectedAt: new Date(),
        rejectionReason: reason,
        reviewedBy: 'admin'
      });

      window.alert(`❌ Request rejected. School will be notified.`);
    } catch (error) {
      console.error('Error rejecting request:', error);
      window.alert(`❌ Error rejecting request: ${error.message}`);
    } finally {
      setProcessing(null);
    }
  };

  const formatDate = (date) => {
    if (!date) return 'Unknown';
    return date.toLocaleDateString() + ' at ' + date.toLocaleTimeString();
  };

  const schoolTypes = [
    'Elementary School', 'Middle School', 'High School', 'K-12 School',
    'Charter School', 'Private School', 'Magnet School', 'Other'
  ];

  const timeZones = [
    { value: 'America/New_York', label: 'Eastern Time' },
    { value: 'America/Chicago', label: 'Central Time' },
    { value: 'America/Denver', label: 'Mountain Time' },
    { value: 'America/Los_Angeles', label: 'Pacific Time' },
    { value: 'America/Anchorage', label: 'Alaska Time' },
    { value: 'Pacific/Honolulu', label: 'Hawaii Time' }
  ];

  const EditableRow = ({ requestId, field, label, value, type = "text", options = null }) => {
    const fieldKey = `${requestId}-${field}`;
    const isEditing = editingField === fieldKey;

    return (
      <tr className={`border-b border-gray-200 ${isEditing ? 'bg-blue-50' : 'hover:bg-gray-50'}`}>
        <td className="py-3 px-4 font-medium text-gray-700 w-1/3">
          {label}
        </td>
        <td className="py-3 px-4 w-1/2">
          {isEditing ? (
            <div className="flex items-center space-x-2">
              {type === 'select' && options ? (
                <select
                  value={editValue}
                  onChange={(e) => setEditValue(e.target.value)}
                  className="flex-1 px-3 py-2 border border-blue-300 rounded focus:outline-none focus:border-blue-500"
                  autoFocus
                >
                  <option value="">Select...</option>
                  {options.map(option => (
                    <option key={option.value || option} value={option.value || option}>
                      {option.label || option}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  type={type}
                  value={editValue}
                  onChange={(e) => setEditValue(e.target.value)}
                  className="flex-1 px-3 py-2 border border-blue-300 rounded focus:outline-none focus:border-blue-500"
                  autoFocus
                />
              )}
            </div>
          ) : (
            <span className="text-gray-900">
              {type === 'select' && options 
                ? options.find(opt => (opt.value || opt) === value)?.label || value || 'Not specified'
                : value || 'Not specified'
              }
            </span>
          )}
        </td>
        <td className="py-3 px-4 w-1/6 text-right">
          {isEditing ? (
            <div className="flex space-x-2 justify-end">
              <button
                onClick={() => saveEdit(requestId, field)}
                className="px-3 py-1 bg-green-600 text-white rounded text-sm hover:bg-green-700"
              >
                ✓ Save
              </button>
              <button
                onClick={cancelEdit}
                className="px-3 py-1 bg-gray-600 text-white rounded text-sm hover:bg-gray-700"
              >
                ✕ Cancel
              </button>
            </div>
          ) : (
            <button
              onClick={() => startEdit(requestId, field, value)}
              disabled={editingField && editingField !== fieldKey}
              className={`px-3 py-1 rounded text-sm ${
                editingField && editingField !== fieldKey
                  ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}
            >
              ✏️ Edit
            </button>
          )}
        </td>
      </tr>
    );
  };

  if (loading) {
    return <div className="text-center py-8">Loading requests...</div>;
  }

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-900">
          Pending School Requests ({requests.length})
        </h2>
        <div className="text-sm text-gray-500">
          Review and edit before approving
        </div>
      </div>

      {requests.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <div className="text-gray-400 text-lg mb-2">📭</div>
          <p className="text-gray-600">No pending requests</p>
        </div>
      ) : (
        <div className="space-y-8">
          {requests.map((request) => (
            <div key={request.id} className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
              {/* Header */}
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-6 py-4 border-b border-gray-200">
                <div className="flex justify-between items-center">
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900">
                      {request.schoolName}
                    </h3>
                    <p className="text-sm text-gray-600 mt-1">
                      Requested: {formatDate(request.submittedAt)}
                    </p>
                  </div>
                  <div className="flex space-x-3">
                    <button
                      onClick={() => handleApprove(request)}
                      disabled={processing === request.id || editingField}
                      className={`px-6 py-3 rounded-lg font-semibold text-sm transition-colors ${
                        processing === request.id || editingField
                          ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                          : 'bg-green-600 text-white hover:bg-green-700 shadow-lg'
                      }`}
                    >
                      {processing === request.id ? '⏳ Creating Tenant...' : '✅ Approve & Create Tenant'}
                    </button>
                    <button
                      onClick={() => handleReject(request)}
                      disabled={processing === request.id || editingField}
                      className={`px-4 py-3 rounded-lg font-semibold text-sm transition-colors ${
                        processing === request.id || editingField
                          ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                          : 'bg-red-600 text-white hover:bg-red-700 shadow-lg'
                      }`}
                    >
                      ❌ Reject
                    </button>
                  </div>
                </div>
                {editingField && (
                  <div className="mt-3 p-3 bg-yellow-100 border border-yellow-300 rounded-md">
                    <p className="text-sm text-yellow-800">
                      ⚠️ Currently editing a field. Please save or cancel before approving.
                    </p>
                  </div>
                )}
              </div>

              {/* Editable Content */}
              <div className="p-6">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  
                  {/* School Information */}
                  <div>
                    <h4 className="text-lg font-semibold text-gray-800 mb-4 pb-2 border-b border-gray-200">
                      🏫 School Information
                    </h4>
                    <table className="w-full">
                      <tbody>
                        <EditableRow
                          requestId={request.id}
                          field="schoolName"
                          label="School Name"
                          value={request.schoolName}
                        />
                        <EditableRow
                          requestId={request.id}
                          field="subdomain"
                          label="Subdomain"
                          value={request.subdomain}
                        />
                        <EditableRow
                          requestId={request.id}
                          field="primaryAdminEmail"
                          label="Admin Email"
                          value={request.primaryAdminEmail}
                          type="email"
                        />
                        <EditableRow
                          requestId={request.id}
                          field="contactPersonName"
                          label="Contact Person"
                          value={request.contactPersonName}
                        />
                        <EditableRow
                          requestId={request.id}
                          field="contactPersonPhone"
                          label="Contact Phone"
                          value={request.contactPersonPhone}
                          type="tel"
                        />
                      </tbody>
                    </table>
                  </div>

                  {/* Organization & Config */}
                  <div>
                    <h4 className="text-lg font-semibold text-gray-800 mb-4 pb-2 border-b border-gray-200">
                      ⚙️ Organization & Configuration
                    </h4>
                    <table className="w-full">
                      <tbody>
                        <EditableRow
                          requestId={request.id}
                          field="schoolType"
                          label="School Type"
                          value={request.schoolType}
                          type="select"
                          options={schoolTypes}
                        />
                        <EditableRow
                          requestId={request.id}
                          field="studentEnrollment"
                          label="Student Enrollment"
                          value={request.studentEnrollment}
                          type="number"
                        />
                        <EditableRow
                          requestId={request.id}
                          field="facultyStaffCount"
                          label="Faculty/Staff Count"
                          value={request.facultyStaffCount}
                          type="number"
                        />
                        <EditableRow
                          requestId={request.id}
                          field="timeZone"
                          label="Time Zone"
                          value={request.timeZone}
                          type="select"
                          options={timeZones}
                        />
                        <EditableRow
                          requestId={request.id}
                          field="academicYearStart"
                          label="Academic Year Start"
                          value={request.academicYearStart}
                          type="date"
                        />
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Additional Information */}
                {(request.allowedEmailDomains?.length > 0 || request.departments?.length > 0 || request.otherIntegrations) && (
                  <div className="mt-8 pt-6 border-t border-gray-200">
                    <h4 className="text-lg font-semibold text-gray-800 mb-4">
                      📋 Additional Information
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {request.allowedEmailDomains?.length > 0 && (
                        <div className="bg-gray-50 p-4 rounded-lg">
                          <h5 className="font-medium text-gray-700 mb-2">Allowed Email Domains</h5>
                          <p className="text-sm text-gray-600">{request.allowedEmailDomains.join(', ')}</p>
                        </div>
                      )}
                      {request.departments?.length > 0 && (
                        <div className="bg-gray-50 p-4 rounded-lg">
                          <h5 className="font-medium text-gray-700 mb-2">Departments</h5>
                          <p className="text-sm text-gray-600">{request.departments.join(', ')}</p>
                        </div>
                      )}
                      {request.otherIntegrations && (
                        <div className="bg-gray-50 p-4 rounded-lg md:col-span-2">
                          <h5 className="font-medium text-gray-700 mb-2">Other Requirements</h5>
                          <p className="text-sm text-gray-600">{request.otherIntegrations}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default TenantRequestsManager;